import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/utils/api";
import { fmt, fmtCurrency, fmtDate, StatusBadge } from "@/utils/format";
import { DASHBOARD } from "@/constants/testIds";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer,
} from "recharts";
import {
  Users, Package, ShoppingCart, TrendingUp,
  CalendarDays, BarChart3, RefreshCw, Clock3,
} from "lucide-react";

// ── Stable Recharts prop objects (defined outside component to prevent re-renders) ──
const CHART_MARGIN = { top: 6, right: 18, bottom: 2, left: 8 };
const AXIS_TICK_STYLE = { fontSize: 11, fill: "#64748b" };
const LINE_DOT_STYLE = { r: 4, fill: "#0D1B3E" };
const TOOLTIP_LABEL_STYLE = { fontSize: 12 };
const MONTH_LABELS = {
  Jan: "Oca", Feb: "Şub", Mar: "Mar", Apr: "Nis", May: "May", Jun: "Haz",
  Jul: "Tem", Aug: "Ağu", Sep: "Eyl", Oct: "Eki", Nov: "Kas", Dec: "Ara",
};

const formatMonth = (value = "") => {
  const [month, year] = String(value).split(" ");
  return `${MONTH_LABELS[month] || month}${year ? ` ${year}` : ""}`;
};

const formatCompactAmount = (value) => {
  const amount = Number(value) || 0;
  const absolute = Math.abs(amount);
  if (absolute >= 1_000_000_000) return `${fmt(amount / 1_000_000_000, 1)} Mr`;
  if (absolute >= 1_000_000) return `${fmt(amount / 1_000_000, 1)} Mn`;
  if (absolute >= 1_000) return `${fmt(amount / 1_000, 0)} B`;
  return fmt(amount, 0);
};

const KPI = ({ label, value, sub, icon: Icon, color, testId }) => (
  <div data-testid={testId} className="min-w-0 rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
    <div className="mb-3 flex items-start justify-between">
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${color}`}>
        <Icon size={18} className="text-white" />
      </div>
    </div>
    <div className="break-words text-2xl font-bold tabular-nums text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>{value}</div>
    <div className="text-sm font-medium text-slate-700 mt-0.5">{label}</div>
    {sub && <div className="mt-1 text-xs leading-relaxed text-slate-400">{sub}</div>}
  </div>
);

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const navigate = useNavigate();

  const fetchDashboard = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const r = await api.get("/dashboard/");
      setData(r.data);
    } catch (e) {
      console.error("Dashboard fetch error:", e);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-[#0D1B3E] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const d = data || {};
  const monthlyData = (d.monthly_ciro || []).map((item) => ({ ...item, ayLabel: formatMonth(item.ay) }));
  const sixMonthTotal = monthlyData.reduce((total, item) => total + (Number(item.ciro) || 0), 0);
  const lastMonth = monthlyData[monthlyData.length - 1];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-[#0D1B3E]" style={{ fontFamily: "Outfit, sans-serif" }}>
            Dashboard
          </h1>
          <p className="mt-0.5 text-sm text-slate-500">Genel bakış ve güncel operasyon özeti</p>
        </div>
        <button type="button" onClick={fetchDashboard} className="inline-flex h-9 items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 text-xs font-semibold text-slate-600 shadow-sm hover:border-slate-300 hover:text-[#0D1B3E]">
          <RefreshCw size={14} /> Yenile
        </button>
      </div>

      {error && <div className="flex items-center justify-between gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"><span>Dashboard bilgileri alınamadı.</span><button type="button" onClick={fetchDashboard} className="font-semibold underline">Tekrar dene</button></div>}

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KPI testId={DASHBOARD.kpiCari} label="Toplam Cari" value={fmt(d.toplam_cari ?? 0, 0)} icon={Users} color="bg-[#0D1B3E]" />
        <KPI testId={DASHBOARD.kpiStok} label="Stok Kalemi" value={fmt(d.toplam_stok ?? 0, 0)} icon={Package} color="bg-blue-600" />
        <KPI testId={DASHBOARD.kpiSiparis} label="Bekleyen Sipariş" value={fmt(d.bekleyen_siparis ?? 0, 0)} icon={ShoppingCart} color="bg-amber-500" />
        {d.stok_degeri != null && <KPI
          testId={DASHBOARD.kpiCiro}
          label="Stok Maliyeti"
          value={fmtCurrency(d.stok_degeri)}
          sub={d.stok_maliyeti_eksik_kalem > 0
            ? `${d.stok_maliyeti_eksik_kalem} stok kaleminde alış maliyeti eksik; toplam eksiktir.`
            : "Alış maliyeti × eldeki miktar (TL)"}
          icon={TrendingUp}
          color="bg-green-600"
        />}
      </div>

      {/* Borç hareketleri + özet */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Chart */}
        <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm lg:col-span-2">
          <div className="mb-4 flex items-start justify-between gap-3">
            <div>
              <h2 className="text-base font-semibold text-slate-800">Aylık Cari Borç Hareketleri</h2>
              <p className="mt-0.5 text-xs text-slate-400">B2B siparişleri dahil değildir · sipariş dışı cari borç kayıtları</p>
            </div>
            <BarChart3 size={18} className="mt-0.5 shrink-0 text-slate-300" />
          </div>
          {monthlyData.length ? (
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={monthlyData} margin={CHART_MARGIN}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="ayLabel" tick={AXIS_TICK_STYLE} tickLine={false} axisLine={{ stroke: "#cbd5e1" }} />
                <YAxis width={66} tick={AXIS_TICK_STYLE} tickLine={false} axisLine={false} tickFormatter={formatCompactAmount} />
                <Tooltip formatter={(v) => [fmtCurrency(v), "Borç hareketi"]} labelStyle={TOOLTIP_LABEL_STYLE} />
                <Line type="monotone" dataKey="ciro" stroke="#0D1B3E" strokeWidth={2} dot={LINE_DOT_STYLE} />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-48 flex items-center justify-center text-slate-400 text-sm">Henüz veri yok</div>
          )}
        </div>

        {/* Özetler */}
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-1">
          <div data-testid={DASHBOARD.eurKuruDisplay} className="rounded-xl bg-[#0D1B3E] p-5 text-white shadow-sm">
            <div className="text-xs text-white/60 uppercase tracking-wider font-semibold mb-1">EUR / TRY Kuru</div>
            <div className="text-3xl font-bold tabular-nums" style={{ fontFamily: "Outfit, sans-serif" }}>
              {fmt(d.eur_kuru, 4)}
            </div>
            <div className="text-xs text-white/50 mt-1">Güncel kur</div>
          </div>
          <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-slate-400"><BarChart3 size={14} /> 6 Aylık Toplam</div>
            <div className="mt-2 text-xl font-bold tabular-nums text-[#0D1B3E]" style={{ fontFamily: "Outfit, sans-serif" }}>{fmtCurrency(sixMonthTotal)}</div>
            <div className="mt-1 text-xs text-slate-400">Sipariş dışı cari borç kayıtları</div>
          </div>
          <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm sm:col-span-2 lg:col-span-1">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-slate-400"><CalendarDays size={14} /> Son Ay</div>
            <div className="mt-2 text-xl font-bold tabular-nums text-[#0D1B3E]" style={{ fontFamily: "Outfit, sans-serif" }}>{fmtCurrency(lastMonth?.ciro || 0)}</div>
            <div className="mt-1 text-xs text-slate-400">{lastMonth?.ayLabel || "Veri yok"}</div>
          </div>
        </div>
      </div>

      {/* Recent Orders + Recently created stock cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Recent Orders */}
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
          <div className="px-5 py-3.5 border-b border-slate-100 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-slate-800">Son Siparişler</h2>
            <button onClick={() => navigate("/siparisler")} className="text-xs text-[#0D1B3E] hover:underline">Tümü</button>
          </div>
          <div data-testid={DASHBOARD.recentOrdersTable}>
            {d.recent_orders?.length ? (
              <table className="w-full text-sm">
                <tbody>
                  {d.recent_orders.map((o) => (
                    <tr key={o.id} className="border-b border-slate-50 hover:bg-slate-50 transition-colors">
                      <td className="px-4 py-3">
                        <div className="font-medium text-slate-800">{o.siparis_no}</div>
                        <div className="text-xs text-slate-400">{o.cari_unvan}</div>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <StatusBadge status={o.durum} />
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums text-sm font-medium text-slate-700">
                        {fmtCurrency(o.toplam_tutar)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="px-5 py-8 text-center text-slate-400 text-sm">Henüz sipariş yok</div>
            )}
          </div>
        </div>

        {/* Recently created stock cards */}
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
          <div className="px-5 py-3.5 border-b border-slate-100 flex items-center gap-2">
            <Clock3 size={15} className="text-blue-500" />
            <h2 className="text-sm font-semibold text-slate-800">Son Eklenen Stok Kartları</h2>
          </div>
          {d.recent_stocks?.length ? (
            <table className="w-full text-sm">
              <tbody>
                {d.recent_stocks.map((s) => (
                  <tr key={s.id} className="border-b border-slate-50 hover:bg-slate-50 transition-colors">
                    <td className="px-4 py-3">
                      <div className="font-medium text-slate-800">{s.kod}</div>
                      <div className="max-w-[240px] truncate text-xs text-slate-400">{s.ad}</div>
                    </td>
                    <td className="px-4 py-3 text-right align-middle">
                      <div className="text-xs font-semibold text-slate-600">{s.marka || "Marka belirtilmemiş"}</div>
                      <div className="mt-0.5 text-[11px] tabular-nums text-slate-400">{fmtDate(s.created_at)}</div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="px-5 py-8 text-center text-slate-400 text-sm">Henüz stok kartı yok</div>
          )}
        </div>
      </div>

      {/* Top Cariler */}
      {d.top_cariler?.length > 0 && (
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
          <div className="px-5 py-3.5 border-b border-slate-100">
            <h2 className="text-sm font-semibold text-slate-800">En Yüksek Bakiyeli Cariler (İlk 10)</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Cari</th>
                  <th className="px-4 py-2.5 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">Bakiye</th>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Fiyat Listesi</th>
                </tr>
              </thead>
              <tbody>
                {d.top_cariler.map((c) => (
                  <tr
                    key={c.id}
                    className="border-b border-slate-100 hover:bg-slate-50 transition-colors cursor-pointer"
                    onClick={() => navigate(`/cariler/${c.id}`)}
                  >
                    <td className="px-4 py-3">
                      <div className="font-medium text-slate-800">{c.unvan}</div>
                      <div className="text-xs text-slate-400">{c.kod}</div>
                    </td>
                    <td className="px-4 py-3 text-right font-semibold tabular-nums text-slate-800">
                      {fmtCurrency(c.bakiye)}
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded">{c.fiyat_listesi || "-"}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
